import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export interface User {
  id: string;
  name: string;
  email: string;
  type: string;
  companyId?: string;
  password?: string;
}

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Fetching users... Current user:', currentUser);
      
      if (!currentUser) {
        console.log('No current user, skipping fetch');
        setIsLoading(false);
        return;
      }

      let query = supabase
        .from('users')
        .select('*');

      // Aplicar filtros baseados no tipo de usuário
      if (currentUser.type === 'admin' && currentUser.companyId) {
        console.log('Admin user, filtering by company:', currentUser.companyId);
        query = query.eq('company_id', currentUser.companyId);
      } else if (currentUser.type === 'user') {
        console.log('Regular user, filtering by own id:', currentUser.id);
        query = query.eq('id', currentUser.id);
      }
      // super_admin vê todos os usuários

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching users:', error);
        throw error;
      }

      console.log('Fetched users:', data);

      const transformedUsers = data?.map((user: any) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        type: user.type,
        companyId: user.company_id,
        password: user.password
      })) || [];

      setUsers(transformedUsers);
    } catch (error: any) {
      console.error('Error in fetchUsers:', error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar usuários",
        description: error.message || "Falha ao buscar lista de usuários",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, toast]);

  const checkEmailExists = async (email: string, userId?: string) => {
    try {
      let query = supabase
        .from('users')
        .select('id')
        .eq('email', email);
      
      if (userId) {
        query = query.neq('id', userId);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      return data && data.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  const addUser = async (userData: Omit<User, "id">) => {
    try {
      console.log('Adding user:', userData);
      
      // Verificar se o email já existe
      const emailExists = await checkEmailExists(userData.email);
      if (emailExists) {
        throw new Error("Este e-mail já está cadastrado");
      }

      // Validar permissões
      if (!currentUser) {
        throw new Error("Usuário não autenticado");
      }

      if (currentUser.type === 'admin') {
        // Admin só pode criar usuários para sua própria empresa
        userData.companyId = currentUser.companyId;
        // Admin não pode criar super_admin
        if (userData.type === 'super_admin') {
          throw new Error("Você não tem permissão para criar super administradores");
        }
      } else if (currentUser.type !== 'super_admin') {
        throw new Error("Você não tem permissão para criar usuários");
      }

      // Primeiro, vamos criar o usuário no sistema de autenticação do Supabase
      let authUser = null;
      if (userData.password) {
        try {
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: userData.email,
            password: userData.password,
          });
          
          if (authError) throw authError;
          
          if (authData?.user) {
            authUser = authData.user;
            console.log('Created auth user:', authUser);
          }
        } catch (authError: any) {
          console.error('Error creating auth user:', authError);
          // Se o erro for que o usuário já existe, continuamos sem criar no auth
          if (!authError.message?.includes('already registered')) {
            throw authError;
          }
        }
      }

      // Agora inserimos na tabela users
      const { data, error } = await supabase
        .from('users')
        .insert([{
          name: userData.name,
          email: userData.email,
          type: userData.type,
          company_id: userData.companyId || null,
          password: userData.password || null,
          created_at: new Date(),
          updated_at: new Date(),
          auth_id: authUser?.id || null
        }])
        .select();

      if (error) {
        console.error('Error details:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error("Erro ao criar usuário: nenhum dado retornado");
      }

      console.log('Added user:', data[0]);

      const newUser: User = {
        id: data[0].id,
        name: data[0].name,
        email: data[0].email,
        type: data[0].type,
        companyId: data[0].company_id,
        password: data[0].password
      };

      setUsers(prev => [newUser, ...prev]);
      
      toast({
        title: "Sucesso",
        description: "Usuário criado com sucesso!",
      });
      
      return newUser;
    } catch (error: any) {
      console.error('Error adding user:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Falha ao adicionar usuário",
      });
      throw error;
    }
  };

  const updateUser = async (id: string, userData: Partial<User>) => {
    try {
      console.log('Updating user:', id, userData);

      if (!currentUser) {
        throw new Error("Usuário não autenticado");
      }

      // Validar permissões
      if (currentUser.type === 'admin') {
        // Admin só pode atualizar usuários da sua empresa
        userData.companyId = currentUser.companyId;
        // Admin não pode criar/atualizar para super_admin
        if (userData.type === 'super_admin') {
          throw new Error("Você não tem permissão para criar super administradores");
        }
      } else if (currentUser.type !== 'super_admin' && id !== currentUser.id) {
        throw new Error("Você não tem permissão para atualizar este usuário");
      }
      
      // Verificar se email já existe
      if (userData.email) {
        const emailExists = await checkEmailExists(userData.email, id);
        if (emailExists) {
          throw new Error("Este e-mail já está cadastrado");
        }
      }

      // Buscar o usuário atual para verificar se tem auth_id
      const { data: currentUserData, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .single();
        
      if (fetchError) {
        throw fetchError;
      }

      // Se tivermos um auth_id e uma nova senha, atualizar no auth
      if (currentUserData.auth_id && userData.password) {
        try {
          // Atualizar senha no sistema de autenticação
          const { error: authUpdateError } = await supabase.auth.admin.updateUserById(
            currentUserData.auth_id,
            { password: userData.password }
          );
          
          if (authUpdateError) {
            console.error('Error updating auth password:', authUpdateError);
            // Ignoramos erro aqui para não impedir atualização do usuário
          }
        } catch (authError) {
          console.error('Failed to update auth password:', authError);
          // Continue com a atualização do usuário mesmo se a senha auth falhar
        }
      }

      const updateData: any = {
        name: userData.name,
        email: userData.email,
        type: userData.type,
        company_id: userData.companyId || null,
        updated_at: new Date()
      };

      // Incluir senha apenas se fornecida
      if (userData.password) {
        updateData.password = userData.password;
      }

      const { error: updateError } = await supabase
        .from('users')
        .update(updateData)
        .eq('id', id);

      if (updateError) {
        console.error('Error updating:', updateError);
        throw updateError;
      }

      // Buscar dados atualizados
      const { data: updatedData, error: fetchUpdateError } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchUpdateError) {
        console.error('Error fetching updated user:', fetchUpdateError);
        throw fetchUpdateError;
      }

      console.log('Updated user:', updatedData);

      const updatedUser: User = {
        id: updatedData.id,
        name: updatedData.name,
        email: updatedData.email,
        type: updatedData.type,
        companyId: updatedData.company_id,
        password: updatedData.password
      };

      setUsers(prev => prev.map(user => user.id === id ? updatedUser : user));
      
      toast({
        title: "Sucesso",
        description: "Usuário atualizado com sucesso!",
      });
      
      return updatedUser;
    } catch (error: any) {
      console.error('Error updating user:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Falha ao atualizar usuário",
      });
      throw error;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      console.log('Deleting user:', id);

      if (!currentUser) {
        throw new Error("Usuário não autenticado");
      }

      // Verificar permissões
      if (currentUser.type === 'admin') {
        // Admin só pode excluir usuários da sua empresa
        const { data: userToDelete } = await supabase
          .from('users')
          .select('*')
          .eq('id', id)
          .single();
          
        if (userToDelete.company_id !== currentUser.companyId) {
          throw new Error("Você não tem permissão para excluir este usuário");
        }
        
        if (userToDelete.type === 'super_admin') {
          throw new Error("Você não tem permissão para excluir super administradores");
        }
      } else if (currentUser.type !== 'super_admin') {
        throw new Error("Você não tem permissão para excluir usuários");
      }

      // Não permitir excluir a si mesmo
      if (id === currentUser.id) {
        throw new Error("Você não pode excluir seu próprio usuário");
      }

      // Buscar o usuário para verificar se tem auth_id
      const { data: userToDelete, error: fetchError } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .single();
        
      if (fetchError) {
        throw fetchError;
      }

      // Se o usuário tiver um auth_id, excluir do sistema de auth também
      if (userToDelete.auth_id) {
        try {
          const { error: authDeleteError } = await supabase.auth.admin.deleteUser(
            userToDelete.auth_id
          );
          
          if (authDeleteError) {
            console.error('Error deleting auth user:', authDeleteError);
            // Continuar mesmo com erro no auth
          }
        } catch (authError) {
          console.error('Failed to delete auth user:', authError);
          // Continuar mesmo com erro no auth
        }
      }

      // Excluir da tabela users
      const { error: deleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', id);

      if (deleteError) {
        console.error('Error deleting user:', deleteError);
        throw deleteError;
      }

      setUsers(prev => prev.filter(user => user.id !== id));
      
      toast({
        title: "Sucesso",
        description: "Usuário excluído com sucesso!",
      });
      
      return true;
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Falha ao excluir usuário",
      });
      throw error;
    }
  };

  useEffect(() => {
    if (currentUser) {
      console.log('useUsers effect: Fetching users because currentUser is available');
      fetchUsers();
    } else {
      console.log('useUsers effect: No currentUser available, not fetching');
      setIsLoading(false);
    }
  }, [currentUser, fetchUsers]); // Added fetchUsers to dependencies

  return {
    users,
    isLoading,
    addUser,
    updateUser,
    deleteUser,
    refreshUsers: fetchUsers, // Expose the fetchUsers function for manual refresh
  };
};
